import React, { useState, useEffect } from "react";
import 'bootstrap/dist/css/bootstrap.min.css';
import { collection, query, where, getDocs } from "firebase/firestore";
import { db, applicationsCol } from "../../firebase";
import { useUser } from "../../UserContext";
import { useNavigate } from 'react-router-dom';


export const ApplicationStatusBoard = () => {
  const { user } = useUser();
  const userID = user?.uid || "";
  const [applications, setApplications] = useState([]);
  const [filteredApplications, setFilteredApplications] = useState([]);
  const [loading, setIsloading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [applicationsPerPage] = useState(5);
  const [searchTerm, setSearchTerm] = useState("");
  const navigate = useNavigate();
  const notSumited = "not submited"
  const submited = "submited"
  const rejected = "rejected"
  const approved = "approoved"
  const selected = "selected"


  useEffect(() => {
    if (!userID) return;
    const fetchApplicantDetails = async () => {
      try {
        setIsloading(true);
        const applicationsRef = collection(db, applicationsCol);
        const applicationsQuery = query(applicationsRef, where('applicantID', '==', userID));
        const querySnapshot = await getDocs(applicationsQuery);
        const applicationsData = querySnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        
        // Sort applications by submission time in descending order (most recent first)
        const sortedApplications = applicationsData.sort((a, b) => {
          const dateA = a.submittedAt?.toDate?.() || new Date(0);
          const dateB = b.submittedAt?.toDate?.() || new Date(0);
          return dateB - dateA; // Descending order
        });
        
        setApplications(sortedApplications);
      } catch (error) {
        console.error("Error fetching applications:", error);
        setApplications([]);
      } finally {
        setIsloading(false);
      }
    };

    fetchApplicantDetails();
  }, [userID]);


  const colorCode = (status) => {
    switch (status) {
      case selected:
        return "#28a745"; // Bootstrap success green
      case approved:
        return "#fd7e14"; // Bootstrap warning orange
      case rejected:
        return "#dc3545"; // Bootstrap danger red
      case submited:
        return "#0d6efd"; // Bootstrap primary blue
      default:
        return "#6c757d"; // Bootstrap secondary gray
    }
  };

  const formatCourses = (application) => {
    const courses = [];
    if (application.firstChoice) {
      const duration = application.firstChoiceDuration ? ` (${application.firstChoiceDuration})` : '';
      courses.push(`1. ${application.firstChoice}${duration}`);
    }
    if (application.secondChoice) {
      const duration = application.secondChoiceDuration ? ` (${application.secondChoiceDuration})` : '';
      courses.push(`2. ${application.secondChoice}${duration}`);
    }
    if (application.thirdChoice) {
      const duration = application.thirdChoiceDuration ? ` (${application.thirdChoiceDuration})` : '';
      courses.push(`3. ${application.thirdChoice}${duration}`);
    }
    return courses;
  };

  const formatApplicantName = (application) => {
    const firstName = application.applicantFirstName || '';
    const middleName = application.applicantMiddleName || '';
    const lastName = application.applicantLastName || '';
    
    const middleInitial = middleName ? middleName.charAt(0).toUpperCase() + '.' : '';
    const fullName = `${firstName} ${middleInitial} ${lastName}`.trim();
    
    return fullName || 'N/A';
  };

  const handleNewApplication = () => {
    // Navigate to the dashboard with personal-info tab active
    navigate('/apply?tab=personal-info');
  };

  // Pagination logic
  const indexOfLastApplication = currentPage * applicationsPerPage;
  const indexOfFirstApplication = indexOfLastApplication - applicationsPerPage;
  const currentApplications = filteredApplications.slice(indexOfFirstApplication, indexOfLastApplication);
  const totalPages = Math.ceil(filteredApplications.length / applicationsPerPage);

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
    // Scroll to top when page changes
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handlePreviousPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handleNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  // Search functionality
  useEffect(() => {
    if (!searchTerm.trim()) {
      setFilteredApplications(applications);
    } else {
      const filtered = applications.filter(application => {
        const searchLower = searchTerm.toLowerCase();
        const applicantName = formatApplicantName(application).toLowerCase();
        const applicationId = application.id.toLowerCase();
        const level = (application.level || '').toLowerCase();
        
        return (
          applicantName.includes(searchLower) ||
          applicationId.includes(searchLower) ||
          level.includes(searchLower)
        );
      });
      setFilteredApplications(filtered);
    }
  }, [searchTerm, applications]);

  // Reset to first page when search term or applications change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, applications.length]);


  //   colorCode(applicantData.status)

  if (loading) return (
    <div className="d-flex justify-content-center align-items-center" style={{ minHeight: "200px" }}>
      <div className="spinner-border text-primary" role="status">
        <span className="visually-hidden">Loading...</span>
      </div>
    </div>
  );

  return (
    <div className="container-fluid mt-4 mb-5 px-3 px-md-4">
      <div className="row">
        <div className="col-12">
          <div className="card shadow-sm border-0">
            <div className="card-header bg-primary text-white">
              <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
                <h4 className="mb-0 fw-bold" style={{ fontSize: 'clamp(1rem, 3vw, 1.25rem)' }}>
                  <i className="fas fa-clipboard-list me-2"></i>
                  <span className="d-none d-sm-inline">APPLICATION STATUS</span>
                  <span className="d-inline d-sm-none">STATUS</span>
                </h4>
                <div className="d-flex align-items-center flex-wrap gap-2">
                  <span className="badge bg-light text-primary fs-6 fw-bold">
                    <i className="fas fa-file-alt me-1"></i>
                    {applications.length} Application{applications.length !== 1 ? 's' : ''}
                  </span>
                  <button 
                    onClick={handleNewApplication}
                    className="btn btn-light fw-bold d-flex align-items-center"
                    style={{ 
                      fontSize: 'clamp(0.75rem, 2vw, 0.875rem)',
                      padding: 'clamp(0.375rem 0.75rem, 1vw 1.5vw, 0.5rem 1rem)'
                    }}
                  >
                    <i className="fas fa-plus me-1 d-none d-sm-inline"></i>
                    <span className="d-none d-md-inline">Add Application</span>
                    <span className="d-inline d-md-none">Add App</span>
                  </button>
                </div>
              </div>
            </div>
            <div className="card-body p-0">
              {filteredApplications.length > 0 ? (
                <>
                  {/* Search Bar */}
                  <div className="p-3 bg-light border-bottom">
                    <div className="row g-2 align-items-center">
                      <div className="col-12 col-md-6">
                        <div className="input-group">
                          <span className="input-group-text bg-white border-end-0">
                            <i className="fas fa-search text-muted"></i>
                          </span>
                          <input
                            type="text"
                            className="form-control border-start-0"
                            placeholder="Search by name, ID, or level..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            style={{ fontSize: 'clamp(0.875rem, 2vw, 1rem)' }}
                          />
                          {searchTerm && (
                            <button
                              className="btn btn-outline-secondary border-start-0"
                              onClick={() => setSearchTerm("")}
                              type="button"
                            >
                              <i className="fas fa-times"></i>
                            </button>
                          )}
                        </div>
                      </div>
                      <div className="col-12 col-md-6">
                        <div className="d-flex justify-content-md-end align-items-center gap-2">
                          <small className="text-muted">
                            {filteredApplications.length} of {applications.length} applications
                          </small>
                          {searchTerm && (
                            <span className="badge bg-primary">
                              <i className="fas fa-filter me-1"></i>
                              Filtered
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Application Summary */}
                  <div className="p-3 bg-light border-bottom">
                    <div className="row g-2 g-md-3">
                      <div className="col-6 col-md-3">
                        <div className="text-center">
                          <div className="h4 mb-1 text-primary fw-bold" style={{ fontSize: 'clamp(1.25rem, 4vw, 1.5rem)' }}>{filteredApplications.length}</div>
                          <small className="text-muted d-none d-sm-inline">Showing</small>
                          <small className="text-muted d-inline d-sm-none">Showing</small>
                        </div>
                      </div>
                      <div className="col-6 col-md-3">
                        <div className="text-center">
                          <div className="h4 mb-1 text-success fw-bold" style={{ fontSize: 'clamp(1.25rem, 4vw, 1.5rem)' }}>
                            {filteredApplications.filter(app => app.status === selected).length}
                          </div>
                          <small className="text-muted">Selected</small>
                        </div>
                      </div>
                      <div className="col-6 col-md-3">
                        <div className="text-center">
                          <div className="h4 mb-1 text-warning fw-bold" style={{ fontSize: 'clamp(1.25rem, 4vw, 1.5rem)' }}>
                            {filteredApplications.filter(app => app.status === approved).length}
                          </div>
                          <small className="text-muted">Approved</small>
                        </div>
                      </div>
                      <div className="col-6 col-md-3">
                        <div className="text-center">
                          <div className="h4 mb-1 text-danger fw-bold" style={{ fontSize: 'clamp(1.25rem, 4vw, 1.5rem)' }}>
                            {filteredApplications.filter(app => app.status === rejected).length}
                          </div>
                          <small className="text-muted">Rejected</small>
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  {/* Applications List */}
                  {currentApplications.map((application, index) => (
                  <div key={application.id} className="mb-4">
                    {/* Desktop Table View */}
                    <div className="d-none d-lg-block">
                      <div className="table-responsive">
                        <table className="table table-hover mb-0">
                          <thead className="table-dark">
                            <tr>
                              <th className="text-center" style={{ width: "10%" }}>Application ID</th>
                              <th className="text-center" style={{ width: "15%" }}>Applicant Name</th>
                              <th className="text-center" style={{ width: "8%" }}>Level</th>
                              <th className="text-center" style={{ width: "15%" }}>University</th>
                              <th className="text-center" style={{ width: "15%" }}>Courses applied</th>
                              {application.status === selected && (
                                <th className="text-center" style={{ width: "12%" }}>Selected Course</th>
                              )}
                              <th className="text-center" style={{ width: "10%" }}>Status</th>
                              <th className="text-center" style={{ width: "15%" }}>Submitted Date</th>
                            </tr>
                          </thead>
                          <tbody>
                            <tr className="align-middle">
                              <td className="text-center fw-bold text-primary">
                                <code className="bg-light px-2 py-1 rounded">{application.id}</code>
                              </td>
                              <td className="text-center fw-semibold">
                                {formatApplicantName(application)}
                              </td>
                              <td className="text-center">
                                <span className="badge bg-secondary px-3 py-2">{application.level==="degree" ? "Bachelor Degree" : "Masters Degree"}</span>
                              </td>
                              <td className="text-center fw-semibold">{application.university}</td>
                              <td className="text-center">
                                <div className="d-flex flex-column gap-1">
                                  {formatCourses(application).map((course, idx) => (
                                    <span key={idx} className="small">
                                      {course}
                                    </span>
                                  ))}
                                </div>
                              </td>
                              {application.status === selected && (
                                <td className="text-center">
                                  <span className="badge bg-success px-3 py-2 fw-bold">
                                    <i className="fas fa-check-circle me-1"></i>
                                    {application.selectedCourse}
                                    {application.selectedCourseDuration && (
                                      <span className="ms-1" style={{ fontSize: '0.85em', opacity: 0.9 }}>
                                        ({application.selectedCourseDuration})
                                      </span>
                                    )}
                                  </span>
                                </td>
                              )}
                              <td className="text-center">
                                <span 
                                  className="badge px-3 py-2 fw-bold"
                                  style={{ 
                                    backgroundColor: colorCode(application.status),
                                    color: 'white'
                                  }}
                                >
                                  {application.status.toUpperCase()}
                                </span>
                              </td>
                              <td className="text-center text-muted">
                                <i className="far fa-calendar-alt me-1"></i>
                                {application.submittedAt.toDate().toLocaleString()}
                              </td>
                            </tr>
                          </tbody>
                        </table>
                      </div>
                    </div>

                    {/* Tablet Table View */}
                    <div className="d-none d-md-block d-lg-none">
                      <div className="table-responsive">
                        <table className="table table-hover mb-0">
                          <thead className="table-dark">
                            <tr>
                              <th className="text-center">App ID</th>
                              <th className="text-center">Name</th>
                              <th className="text-center">Level</th>
                              <th className="text-center">University</th>
                              <th className="text-center">Courses</th>
                              {application.status === selected && (
                                <th className="text-center">Selected</th>
                              )}
                              <th className="text-center">Status</th>
                              <th className="text-center">Date</th>
                            </tr>
                          </thead>
                          <tbody>
                            <tr className="align-middle">
                              <td className="text-center fw-bold text-primary">
                                <code className="bg-light px-1 py-1 rounded small">{application.id}</code>
                              </td>
                              <td className="text-center fw-semibold small">
                                {formatApplicantName(application)}
                              </td>
                              <td className="text-center">
                                <span className="badge bg-secondary px-2 py-1 small">{application.level}</span>
                              </td>
                              <td className="text-center fw-semibold small">{application.university}</td>
                              <td className="text-center small">
                                <div className="d-flex flex-column gap-1">
                                  {formatCourses(application).map((course, idx) => (
                                    <span key={idx} className="small">
                                      {course}
                                    </span>
                                  ))}
                                </div>
                              </td>
                              {application.status === selected && (
                                <td className="text-center">
                                  <span className="badge bg-success px-2 py-1 fw-bold small">
                                    <i className="fas fa-check-circle me-1"></i>
                                    {application.selectedCourse}
                                    {application.selectedCourseDuration && (
                                      <span className="ms-1" style={{ fontSize: '0.8em', opacity: 0.9 }}>
                                        ({application.selectedCourseDuration})
                                      </span>
                                    )}
                                  </span>
                                </td>
                              )}
                              <td className="text-center">
                                <span 
                                  className="badge px-2 py-1 fw-bold small"
                                  style={{ 
                                    backgroundColor: colorCode(application.status),
                                    color: 'white'
                                  }}
                                >
                                  {application.status.toUpperCase()}
                                </span>
                              </td>
                              <td className="text-center text-muted small">
                                <i className="far fa-calendar-alt me-1"></i>
                                {application.submittedAt.toDate().toLocaleString()}
                              </td>
                            </tr>
                          </tbody>
                        </table>
                      </div>
                    </div>

                    {/* Mobile Card View */}
                    <div className="d-block d-md-none">
                      <div className="card border-0 shadow-sm mx-2">
                        <div className="card-header bg-light py-2">
                          <div className="d-flex justify-content-between align-items-center">
                            <span className="fw-bold text-primary small">
                              <code className="bg-light px-2 py-1 rounded">{application.id}</code>
                            </span>
                            <span 
                              className="badge px-2 py-1 fw-bold small"
                              style={{ 
                                backgroundColor: colorCode(application.status),
                                color: 'white'
                              }}
                            >
                              {application.status.toUpperCase()}
                            </span>
                          </div>
                        </div>
                        <div className="card-body py-3">
                          <div className="row g-2">
                            <div className="col-12 mb-2">
                              <div className="d-flex flex-column">
                                <small className="text-muted fw-bold">Applicant Name</small>
                                <span className="fw-semibold">{formatApplicantName(application)}</span>
                              </div>
                            </div>
                            <div className="col-6">
                              <div className="d-flex flex-column">
                                <small className="text-muted fw-bold">Level</small>
                                <span className="badge bg-secondary px-2 py-1 small w-fit-content">{application.level}</span>
                              </div>
                            </div>
                            <div className="col-6">
                              <div className="d-flex flex-column">
                                <small className="text-muted fw-bold">Submitted Date</small>
                                <span className="small">
                                  <i className="far fa-calendar-alt me-1"></i>
                                  {application.submittedAt.toDate().toLocaleString()}
                                </span>
                              </div>
                            </div>
                            <div className="col-12 mt-2">
                              <div className="d-flex flex-column">
                                <small className="text-muted fw-bold">University</small>
                                <span className="fw-semibold">{application.university}</span>
                              </div>
                            </div>
                            <div className="col-12 mt-2">
                              <div className="d-flex flex-column">
                                <small className="text-muted fw-bold">Courses applied</small>
                                <div className="d-flex flex-column gap-1 mt-1">
                                  {formatCourses(application).map((course, idx) => (
                                    <span key={idx} className="small">
                                      {course}
                                    </span>
                                  ))}
                                </div>
                              </div>
                            </div>
                            {application.status === selected && (
                              <div className="col-12 mt-2">
                                <div className="d-flex flex-column">
                                  <small className="text-muted fw-bold">Selected Course</small>
                                  <span className="badge bg-success px-3 py-2 fw-bold mt-1">
                                    <i className="fas fa-check-circle me-1"></i>
                                    {application.selectedCourse}
                                  </span>
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>

                    {index < currentApplications.length - 1 && (
                      <hr className="my-3 d-none d-lg-block" />
                    )}
                  </div>
                ))}

                {/* Pagination */}
                {totalPages > 1 && (
                  <div className="d-flex justify-content-center align-items-center py-4 border-top">
                    <nav aria-label="Application pagination">
                      <ul className="pagination pagination-sm mb-0">
                        {/* Previous Button */}
                        <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                          <button
                            className="page-link"
                            onClick={handlePreviousPage}
                            disabled={currentPage === 1}
                            aria-label="Previous page"
                          >
                            <i className="fas fa-chevron-left"></i>
                          </button>
                        </li>

                        {/* Page Numbers */}
                        {Array.from({ length: totalPages }, (_, index) => {
                          const pageNumber = index + 1;
                          const isCurrentPage = pageNumber === currentPage;
                          
                          // Show first page, last page, current page, and pages around current page
                          if (
                            pageNumber === 1 ||
                            pageNumber === totalPages ||
                            (pageNumber >= currentPage - 1 && pageNumber <= currentPage + 1)
                          ) {
                            return (
                              <li key={pageNumber} className={`page-item ${isCurrentPage ? 'active' : ''}`}>
                                <button
                                  className="page-link"
                                  onClick={() => handlePageChange(pageNumber)}
                                  style={{ 
                                    minWidth: '40px',
                                    fontSize: 'clamp(0.75rem, 2vw, 0.875rem)'
                                  }}
                                >
                                  {pageNumber}
                                </button>
                              </li>
                            );
                          } else if (
                            pageNumber === currentPage - 2 ||
                            pageNumber === currentPage + 2
                          ) {
                            return (
                              <li key={pageNumber} className="page-item disabled">
                                <span className="page-link">...</span>
                              </li>
                            );
                          }
                          return null;
                        })}

                        {/* Next Button */}
                        <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                          <button
                            className="page-link"
                            onClick={handleNextPage}
                            disabled={currentPage === totalPages}
                            aria-label="Next page"
                          >
                            <i className="fas fa-chevron-right"></i>
                          </button>
                        </li>
                      </ul>
                    </nav>
                    
                    {/* Page Info */}
                    <div className="ms-3 text-muted small d-none d-md-block">
                      <span>
                        Showing {indexOfFirstApplication + 1} to {Math.min(indexOfLastApplication, filteredApplications.length)} of {filteredApplications.length} applications
                      </span>
                    </div>
                  </div>
                )}
                </>
              ) : (
                <div className="text-center py-4 py-md-5">
                  <div className="alert alert-info border-0 shadow-sm mx-2 mx-md-3">
                    <i className="fas fa-info-circle me-2"></i>
                    <strong>
                      {applications.length === 0 ? 'No applications found' : 'No matching applications'}
                    </strong>
                    <p className="mb-0 mt-2 text-muted">
                      {applications.length === 0 
                        ? "You haven't submitted any applications yet."
                        : "Try adjusting your search terms or clear the search to see all applications."
                      }
                    </p>
                    {applications.length === 0 ? (
                      <button 
                        onClick={handleNewApplication}
                        className="btn btn-primary mt-3"
                        style={{ fontSize: 'clamp(0.875rem, 2.5vw, 1rem)' }}
                      >
                        <i className="fas fa-plus me-2"></i>
                        Start Your First Application
                      </button>
                    ) : (
                      <button 
                        onClick={() => setSearchTerm("")}
                        className="btn btn-outline-primary mt-3"
                        style={{ fontSize: 'clamp(0.875rem, 2.5vw, 1rem)' }}
                      >
                        <i className="fas fa-times me-2"></i>
                        Clear Search
                      </button>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};




